import { Controller } from "@hotwired/stimulus"

// Client-side resize + centre crop for profile pictures.
//
// There is no image processor on the server — no libvips binding, no
// ImageMagick, no `image_processing` gem — so ActiveStorage variants are not
// available and the bytes we store are the bytes we serve. Rather than store a
// 6MB phone photo and scale it with CSS forever, we redraw the chosen file to a
// 512x512 canvas here and upload that instead. A phone on a bad connection
// sends ~60KB rather than several megabytes, and the stored blob is already the
// right shape for a round avatar.
//
// The server does not trust any of this: User validates the byte size and
// sniffs the real content type of whatever actually arrives.
const SIZE = 512
const QUALITY = 0.85
const ALLOWED = ["image/jpeg", "image/png", "image/webp"]
const MAX_INPUT_BYTES = 25 * 1024 * 1024

export default class extends Controller {
  static targets = ["input", "preview", "current", "actions", "form", "error"]

  connect() {
    // Progressive enhancement: the markup ships with the submit button visible
    // so a no-JS browser can still post the original file. Once we're running,
    // it only appears when there's something to save.
    if (this.hasActionsTarget) this.actionsTarget.hidden = true
    this.objectUrl = null
  }

  disconnect() {
    this.revokePreview()
  }

  async fileChosen(event) {
    const file = event.target.files && event.target.files[0]
    if (!file) return this.cancel()

    this.clearError()

    if (!ALLOWED.includes(file.type)) {
      return this.fail("Choose a JPEG, PNG, or WebP image.")
    }
    if (file.size > MAX_INPUT_BYTES) {
      return this.fail(
        "That image is too large to work with. Try a smaller one."
      )
    }

    try {
      const blob = await this.squareBlob(file)
      const resized = new File([blob], this.outputName(file), {
        type: "image/jpeg",
        lastModified: Date.now(),
      })

      // Swap the resized file back into the input so the ordinary multipart
      // form submit carries it. No direct-upload endpoint, no JSON API.
      const transfer = new DataTransfer()
      transfer.items.add(resized)
      this.inputTarget.files = transfer.files

      this.showPreview(resized)
    } catch (error) {
      console.error(error)
      this.fail("That image couldn't be read. Try a different one.")
    }
  }

  cancel() {
    this.inputTarget.value = ""
    this.revokePreview()
    if (this.hasPreviewTarget) this.previewTarget.hidden = true
    if (this.hasCurrentTarget) this.currentTarget.hidden = false
    if (this.hasActionsTarget) this.actionsTarget.hidden = true
    this.clearError()
  }

  // --- internals -----------------------------------------------------------

  async squareBlob(file) {
    const source = await this.decode(file)
    const side = Math.min(source.width, source.height)
    const sx = (source.width - side) / 2
    const sy = (source.height - side) / 2

    const canvas = document.createElement("canvas")
    canvas.width = SIZE
    canvas.height = SIZE
    const context = canvas.getContext("2d")
    // White ground: the output is JPEG, which has no alpha, and an unset canvas
    // would otherwise flatten transparent PNGs to black.
    context.fillStyle = "#ffffff"
    context.fillRect(0, 0, SIZE, SIZE)
    context.imageSmoothingQuality = "high"
    context.drawImage(source, sx, sy, side, side, 0, 0, SIZE, SIZE)

    if (source.close) source.close()

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) =>
          blob
            ? resolve(blob)
            : reject(new Error("canvas.toBlob returned null")),
        "image/jpeg",
        QUALITY
      )
    })
  }

  // createImageBitmap honours EXIF orientation with imageOrientation:"from-image",
  // which matters a lot for phone photos. Safari <15 lacks the option, so fall
  // back to an <img>, which applies orientation itself in modern browsers.
  async decode(file) {
    if (typeof createImageBitmap === "function") {
      try {
        return await createImageBitmap(file, { imageOrientation: "from-image" })
      } catch (_error) {
        // fall through to the <img> path
      }
    }

    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file)
      const image = new Image()
      image.onload = () => {
        URL.revokeObjectURL(url)
        resolve(image)
      }
      image.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error("image decode failed"))
      }
      image.src = url
    })
  }

  showPreview(file) {
    this.revokePreview()
    this.objectUrl = URL.createObjectURL(file)

    if (this.hasPreviewTarget) {
      this.previewTarget.src = this.objectUrl
      this.previewTarget.hidden = false
    }
    if (this.hasCurrentTarget) this.currentTarget.hidden = true
    if (this.hasActionsTarget) this.actionsTarget.hidden = false
  }

  revokePreview() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl)
      this.objectUrl = null
    }
  }

  outputName(file) {
    const stem = (file.name || "avatar").replace(/\.[^./\\]+$/, "").slice(0, 40)
    return `${stem || "avatar"}.jpg`
  }

  fail(message) {
    this.inputTarget.value = ""
    if (this.hasActionsTarget) this.actionsTarget.hidden = true
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.hidden = false
    }
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = ""
      this.errorTarget.hidden = true
    }
  }
}
